import type { FC, ReactNode } from 'react'

import type { WhiteLabel } from '@skytech/types'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { twJoin, twMerge } from 'tailwind-merge'

type NotificationBubbleVariant = 'primary' | 'secondary'

export const NotificationBubble: FC<{
  children?: ReactNode
  wrapperClassName?: string
  className?: string
  variant?: NotificationBubbleVariant
  isDisabled?: boolean
  selected?: boolean
  amount?: number
  whiteLabel?: WhiteLabel
  isDealer?: boolean
}> = ({
  children,
  wrapperClassName = '',
  className = '',
  variant = 'primary',
  isDisabled = false,
  selected = false,
  amount,
  whiteLabel: _whiteLabel,
  isDealer,
}) => {
  // todo: replace with the org from url when ready
  const storedWhiteLabel = localStorage.getItem('org') as WhiteLabel
  const effectiveWhiteLabel = _whiteLabel || storedWhiteLabel
  const getWhiteLabel = (): WhiteLabel => {
    if (!isDealer) return effectiveWhiteLabel
    return getWhiteLabelForDealerStyle(effectiveWhiteLabel)
  }

  const whiteLabel = getWhiteLabel()

  return (
    <div className={twJoin('relative ml-[0.5em] inline-block w-[1.6em]', wrapperClassName)}>
      <div
        className={twMerge(
          `bg-primary absolute -top-[1.15em] flex size-[1.6em] items-center justify-center rounded-full text-white ${className}`,
          whiteLabelVariantColorClasses[whiteLabel]?.[variant],
          variant === 'secondary' &&
            (selected
              ? whiteLabelVariantColorClasses[whiteLabel]?.[variant]
              : 'bg-gray-200 text-gray-600'),
          isDisabled && 'bg-zinc-100 text-gray-400'
        )}
      >
        {amount && (
          <span className={twJoin(amount >= 100 && 'text-[0.75em]')}>
            {amount < 1000 ? amount : '1k+'}
          </span>
        )}
        {children}
      </div>
    </div>
  )
}

const whiteLabelVariantColorClasses: Partial<
  Record<WhiteLabel, Record<NotificationBubbleVariant, string>>
> = {
  control: {
    primary: 'bg-skytech-lightOrange',
    secondary: 'bg-skytech-lightOrange text-white',
  },
  skytech: {
    primary: 'bg-skytech-lightOrange',
    secondary: 'bg-skytech-lightOrange text-white',
  },
  tcm: {
    primary: 'bg-skytech-lightOrange',
    secondary: 'bg-skytech-lightOrange text-white',
  },
  ice: {
    primary: 'bg-[#ffb901]',
    secondary: 'bg-[#fee283] text-slate-600',
  },
  mobit: {
    primary: 'bg-mobit',
    secondary: 'bg-mobit-brightBlue text-mobit',
  },
  bechtle: {
    primary: 'bg-bechtle',
    secondary: 'bg-[#98d6b5] text-bechtle-text',
  },
  csi: {
    primary: 'bg-csi-maroon',
    secondary: 'bg-[#fee5eb] text-black',
  },
  elkjop: {
    primary: 'bg-elkjop-baseGreen',
    secondary: 'bg-[#def3c2] text-elkjop',
  },
  tietoevry: {
    primary: 'bg-tietoevry',
    secondary: 'bg-[#d8efff] text-black',
  },
  evry: {
    primary: 'bg-tietoevry',
    secondary: 'bg-[#d8efff] text-black',
  },
  telia: {
    primary: 'bg-telia',
    secondary: 'bg-[#f4e5fb] text-grayText',
  },
  arp: {
    primary: 'bg-arp',
    secondary: 'bg-[#b2ebed] text-black',
  },
  threeStepIT: {
    primary: 'bg-threeStepIT',
    secondary: 'bg-threeStepIT-lightBeige text-threeStepIT-black',
  },
  komplett: {
    primary: 'bg-komplett-lightYellow text-komplett',
    secondary: 'bg-[#fef7c0] text-komplett',
  },
}
